import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/atlassian/pipelines/agent/build/platform/services/design-system-docs/src/__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/templates/page.tsx";
import Link from '@atlaskit/link';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import lessBoldLight from './_assets/voice-and-tone-less-bold-light.png';
import lessBoldDark from './_assets/voice-and-tone-less-bold-dark.png';
import moreBoldLight from './_assets/voice-and-tone-more-bold-light.png';
import moreBoldDark from './_assets/voice-and-tone-more-bold-dark.png';
import lessOptimisticLight from './_assets/voice-and-tone-less-optimistic-light.png';
import lessOptimisticDark from './_assets/voice-and-tone-less-optimistic-dark.png';
import moreOptimisticLight from './_assets/voice-and-tone-more-optimistic-light.png';
import moreOptimisticDark from './_assets/voice-and-tone-more-optimistic-dark.png';
import lessWinkyLight from './_assets/voice-and-tone-less-winky-light.png';
import lessWinkyDark from './_assets/voice-and-tone-less-winky-dark.png';
import moreWinkyLight from './_assets/voice-and-tone-more-winky-light.png';
import moreWinkyDark from './_assets/voice-and-tone-more-winky-dark.png';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <p>{`We use the Atlassian voice and tone in all of our properties, and product content, from product user
interfaces to email, social media, and other channels.`}</p>
    <p>{`Any Atlassian who works with customer-facing communications should follow the voice and tone
guidelines: content designers, engineers, product designers, blog contributors, product managers,
marketers... pretty much everyone.`}</p>
    <p>{`To help teams do the best work of their lives, we:`}</p>
    <ul>
      <li parentName="ul">{`offer solutions that help people at the moment they need it`}</li>
      <li parentName="ul">{`simplify complex problems into easy-to-understand pieces`}</li>
      <li parentName="ul">{`inspire people to try new things`}</li>
    </ul>
    <p>{`With a familiar tone, clear language, and a solid knowledge of our audience, we craft messages that
get teams moving in the right direction, then we get out of their way.`}</p>
    <p>{`The Atlassian voice is based on the Atlassian brand personality traits of:`}</p>
    <ul>
      <li parentName="ul">{`bold`}</li>
      <li parentName="ul">{`optimistic`}</li>
      <li parentName="ul">{`practical, with a wink`}</li>
    </ul>
    <p>{`To provide consistent, friendly, and helpful content for people, use this guidance with our other
guidelines:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/language-and-grammar"
        }}>{`Language and grammar`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/inclusive-writing"
        }}>{`Inclusive language`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://go.atlassian.com/vocab"
        }}>{`Vocabulary (Atlassians only)`}</a></li>
    </ul>
    <h2 {...{
      "id": "atlassians-personality-traits"
    }}>{`Atlassian’s personality traits`}</h2>
    <p>{`When writing with Atlassian's voice, we're part of the team. We're that friend or colleague who is
always up on new trends and wants to be helpful and share new wisdom with everyone in a relatable
way.`}</p>
    <p>{`We know when it's time to be serious and direct, and when it's time to be more casual.`}</p>
    <p>{`Personality comes across best in the details. The emphasis of each trait should be contextual to the
audience and situation.`}</p>
    <p>{`Here are some questions you can ask yourself during your process to assess whether or not you’re
leveraging our personality traits well.`}</p>
    <ul>
      <li parentName="ul">{`What is the emotional state of the person when encountering your solution during their journey?`}</li>
      <li parentName="ul">{`How might our personality traits help enhance a moment of celebration?`}</li>
      <li parentName="ul">{`How might our personality traits help diffuse a moment of frustration or pain?`}</li>
    </ul>
    <h3 {...{
      "id": "bold"
    }}>{`Bold`}</h3>
    <p>{`Motivate teams to do their best work. Offer best practices to get people going in the right
direction.`}</p>
    <p>{`Be bold and offer just enough help to get the work started, and then get out of the way.`}</p>
    <p>{`Give accurate information so people can make educated decisions. Understand the person's struggles
and desired outcomes and give enough information to let them get where they need to go.`}</p>
    <h3 {...{
      "id": "optimistic"
    }}>{`Optimistic`}</h3>
    <p>{`Go on the journey with people and highlight the key points that will help them the most, right now.`}</p>
    <p>{`Be in the moment by focusing attention on the important bits first. This gives people confidence in
our products.`}</p>
    <p>{`Weave a consistent story across our fabric and be diligent about vocabulary across all messaging by
being brand-conscious across products.`}</p>
    <p>{`Create a seamless flow across all the things. Let people know that they can jump in and start
working, and can expect a familiar experience across all of the things. Keep teams in the loop about
what is happening by informing them of relevant features, products and opportunities for success.`}</p>
    <h3 {...{
      "id": "practical-with-a-wink"
    }}>{`Practical, with a wink`}</h3>
    <p>{`Get to the point and be direct. Be concise.`}</p>
    <p>{`Be on the lookout for opportunities and be quick to offer a helping hand. Give the user just enough
to know that something awesome is around the corner and then get out of the way.`}</p>
    <p>{`Write clear, accurate, and concise text that makes interfaces more usable and consistent — and
builds trust.`}</p>
    <p>{`Write text that is understandable by anyone, anywhere, regardless of their culture or language so
that everyone feels they are part of the team.`}</p>
    <h2 {...{
      "id": "voice-and-tone-principles"
    }}>{`Voice and tone principles`}</h2>
    <p>{`A consistent voice creates greater quality products and better relationships with our customers.`}</p>
    <p>{`But, there is one caveat.`}</p>
    <p>{`There are times when you need to speak to different subsets of people, for instance among different
products, and your tone might not be quite the same. That's okay! We've provided enough guidance to
help adjust our tone to accommodate for:`}</p>
    <ul>
      <li parentName="ul">{`where people are at in their journey`}</li>
      <li parentName="ul">{`their emotional state`}</li>
      <li parentName="ul">{`product or property you are writing for`}</li>
    </ul>
    <p>{`The voice and tone principles:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/voice-tone#inform-to-build-trust"
        }}>{`Inform to build trust`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/voice-tone#empower-to-inspire-action"
        }}>{`Empower to inspire action`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/voice-tone#encourage-people-along-the-path"
        }}>{`Encourage people along the path`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/voice-tone#motivate-by-showing-possibilities"
        }}>{`Motivate by showing possibilities`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/voice-tone#satisfy-by-meeting-expectations"
        }}>{`Satisfy by meeting expectations`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/voice-tone#delight-with-unexpectedly-pleasing-experiences"
        }}>{`Delight with unexpectedly pleasing experiences`}</a></li>
    </ul>
    <h3 {...{
      "id": "inform-to-build-trust"
    }}>{`Inform to build trust`}</h3>
    <p><strong parentName="p">{`Inform`}</strong>{` by being `}<strong parentName="p">{`open`}</strong>{` and `}<strong parentName="p">{`clear`}</strong>{` on what people are experiencing with our products.`}</p>
    <p>{`It's about telling them what they need to know at that moment and nothing more. Be aware of when a
user may be new or confused, and tone down the boldness by being more prescriptive. Let people know
where they are in their journey and what they are looking at in the product.`}</p>
    <p>{`Write as a knowledgable member of the team. Show up at the right time and be open, humble, and warm
— offer direction for the most appropriate next steps and get out of the way.`}</p>
    <h4 {...{
      "id": "when-we-need-to-be-less-bold"
    }}>{`When we need to be less bold`}</h4>
    <p>{`Person is feeling: apprehension, confusion, annoyance, fear, loathing, anger.`}</p>
    <p>{`Examples: new users, evaluators, and when introducing a new concept, feature, or product`}</p>
    <img src={lessBoldLight} srcDark={lessBoldDark} alt="Slider between less bold and more bold, with the dot much closer to less bold end." />
    <h4 {...{
      "id": "places-we-use-this-principle"
    }}>{`Places we use this principle`}</h4>
    <ul>
      <li parentName="ul">{`In product: `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/flag/usage"
        }}>{`flags`}</a>{`,
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/error-messages"
        }}>{`error messages`}</a>{`,
and `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/onboarding/usage"
        }}>{`onboarding (spotlight)`}</a></li>
      <li parentName="ul">{`Being informative and to build trust`}</li>
    </ul>
    <h3 {...{
      "id": "empower-to-inspire-action"
    }}>{`Empower to inspire action`}</h3>
    <p><strong parentName="p">{`Educate`}</strong>{` where we people need it most. Offer `}<strong parentName="p">{`opportunities`}</strong>{` to learn at pivotal times to
`}<strong parentName="p">{`empower`}</strong>{` people to move in the right direction. Offer best practices and recommendations for next
steps while suggesting ways to improve and make decisions.`}</p>
    <p>{`Let people know Atlassian and fellow community members are available to help people make decisions.`}</p>
    <p>{`Write as if you are educating. You are a teacher with empathy and an understanding of what it’s like
to be in the weeds. You expect your audience to have a basic understanding.`}</p>
    <h4 {...{
      "id": "when-we-want-to-be-more-bold"
    }}>{`When we want to be more bold`}</h4>
    <p>{`Person is feeling: confident, interested, trust, anticipation.`}</p>
    <p>{`Examples: power users, admins, everyday users`}</p>
    <img src={moreBoldLight} srcDark={moreBoldDark} alt="Slider between less bold and more bold, with the dot much closer to more bold end." />
    <h4 {...{
      "id": "places-we-use-this-principle-1"
    }}>{`Places we use this principle`}</h4>
    <ul>
      <li parentName="ul">{`In product: `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/onboarding/usage"
        }}>{`onboarding (spotlight)`}</a>{`,
`}<a parentName="li" {...{
          "href": "https://atlassian.design/components/onboarding/benefits-modal/usage"
        }}>{`benefits modal`}</a>{`, and
`}<a parentName="li" {...{
          "href": "https://atlassian.design/components/modal-dialog/usage"
        }}>{`modal dialogs`}</a></li>
      <li parentName="ul">{`Something requires an action from the person`}</li>
      <li parentName="ul">{`Educational opportunities`}</li>
      <li parentName="ul">{`Social opportunities — let them know best practices`}</li>
    </ul>
    <h3 {...{
      "id": "encourage-people-along-the-path"
    }}>{`Encourage people along the path`}</h3>
    <p><strong parentName="p">{`Inspire`}</strong>{` initial `}<strong parentName="p">{`optimism`}</strong>{` by providing `}<strong parentName="p">{`support`}</strong>{` in the right place. Be `}<strong parentName="p">{`consistent`}</strong>{` and
`}<strong parentName="p">{`dependable`}</strong>{`.`}</p>
    <p>{`Offer waypoints, help, and support if people are feeling confused or frustrated at a point in their
journey. This principle is about being human, giving guidance, support, and encouragement along the
way.`}</p>
    <p>{`Guide people by revealing information gracefully. Let them know they are on the right path, they
aren’t alone, and that delivering projects and building teams can be challenging.`}</p>
    <p>{`Write in an upbeat, friendly way. Acknowledge the opportunities in the here-and-now and walk through
it with them.`}</p>
    <h4 {...{
      "id": "when-we-need-to-be-less-optimistic"
    }}>{`When we need to be less optimistic`}</h4>
    <p>{`Person is feeling: anticipation, unsupported, confused, uncertain`}</p>
    <p>{`Examples: new to Atlassian, evaluators, or when introducing a new concept, feature, or product`}</p>
    <img src={lessOptimisticLight} srcDark={lessOptimisticDark} alt="Slider between less optimistic and more optimistic, with the dot much closer to the less optimistic end." />
    <h4 {...{
      "id": "places-we-use-this-principle-2"
    }}>{`Places we use this principle`}</h4>
    <ul>
      <li parentName="ul">{`In product:
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/info-messages"
        }}>{`information messages`}</a>{`,
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/error-messages"
        }}>{`error messages`}</a>{`,
and `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/section-message/usage"
        }}>{`section messages`}</a></li>
      <li parentName="ul">{`Something requires an action from the person`}</li>
    </ul>
    <h3 {...{
      "id": "motivate-by-showing-possibilities"
    }}>{`Motivate by showing possibilities`}</h3>
    <p>{`Provide `}<strong parentName="p">{`incentive`}</strong>{` and `}<strong parentName="p">{`excitement`}</strong>{` for continued growth.`}</p>
    <p>{`Consider why someone would want to do this.`}</p>
    <p>{`Show the possibilities of what can be accomplished by giving examples of how other people accomplish
the same task, or by presenting the ideal state. Describe the end result, giving expert testimony,
or offer opportunities for advanced knowledge.`}</p>
    <p>{`Write like an expert. Focus more on the solution than on the problem. Show people the possible
benefits.`}</p>
    <h4 {...{
      "id": "when-we-want-to-be-more-optimistic"
    }}>{`When we want to be more optimistic`}</h4>
    <p>{`Person is feeling: ambitious, inspired, curious, admiration.`}</p>
    <p>{`Examples: power users, admins, everyday users`}</p>
    <img src={moreOptimisticLight} srcDark={moreOptimisticDark} alt="Slider between less optimistic and more optimistic, with the dot much closer to the more optimistic end." />
    <h4 {...{
      "id": "places-we-use-this-principle-3"
    }}>{`Places we use this principle`}</h4>
    <ul>
      <li parentName="ul">{`In product: `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/onboarding/usage"
        }}>{`onboarding (spotlight)`}</a>{` and
`}<a parentName="li" {...{
          "href": "https://atlassian.design/components/modal-dialog/usage"
        }}>{`modal dialogs`}</a></li>
      <li parentName="ul">{`Educational opportunities — it's a chance to tell them how other industry leaders do it, give best
practices, and tips and tricks`}</li>
    </ul>
    <h3 {...{
      "id": "satisfy-by-meeting-expectations"
    }}>{`Satisfy by meeting expectations`}</h3>
    <p>{`Give people what they need.`}</p>
    <p>{`Provide `}<strong parentName="p">{`quick and thorough`}</strong>{` answers, guidance, actions, and instructions. We aren’t trying to
lead, inspire, motivate, delight, or encourage. Simply tell people what they need to know and get
out of the way.`}</p>
    <p>{`All of our content should be practical at a minimum. Write as if you are explaining to your friend
how to travel somewhere you’ve visited regularly.`}</p>
    <h4 {...{
      "id": "when-we-need-to-be-practical"
    }}>{`When we need to be practical`}</h4>
    <p>{`Person is feeling: overwhelmed and stressed.`}</p>
    <p>{`Examples: people under pressure with deadlines — everyone feels this way sometimes`}</p>
    <img src={lessWinkyLight} srcDark={lessWinkyDark} alt="Slider between practical and winky, with the dot much closer to the practical end." />
    <h4 {...{
      "id": "places-we-use-this-principle-4"
    }}>{`Places we use this principle`}</h4>
    <ul>
      <li parentName="ul">{`Whenever we can`}</li>
      <li parentName="ul">{`In product:
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/warning-messages"
        }}>{`warning messages`}</a>{`,
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/info-messages"
        }}>{`information messages`}</a>{`,
and
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/error-messages"
        }}>{`error messages`}</a></li>
    </ul>
    <h3 {...{
      "id": "delight-with-unexpectedly-pleasing-experiences"
    }}>{`Delight with unexpectedly pleasing experiences`}</h3>
    <p>{`“Wink” where appropriate. We deliver appropriate `}<strong parentName="p">{`delight`}</strong>{` — celebrate `}<strong parentName="p">{`success`}</strong>{` or `}<strong parentName="p">{`progress`}</strong>{`
once we’ve built `}<strong parentName="p">{`trust`}</strong>{`.`}</p>
    <p>{`But don’t overdo it. These are little flourishes: we give flowers, not puppies.`}</p>
    <p>{`Pay particular attention to people’s state of mind. Think about the timing, and how frequently they
will see this.`}</p>
    <h4 {...{
      "id": "when-we-can-share-a-little-delight"
    }}>{`When we can share a little delight`}</h4>
    <p>{`Person is feeling: successful, joy, pride, relief.`}</p>
    <p>{`Examples: evaluators, power users, during social interactions`}</p>
    <img src={moreWinkyLight} srcDark={moreWinkyDark} alt="Slider between practical and winky, with the dot much closer to the winky end." />
    <h4 {...{
      "id": "places-we-use-this-principle-5"
    }}>{`Places we use this principle`}</h4>
    <ul>
      <li parentName="ul">{`In product:
`}<a parentName="li" {...{
          "href": "https://atlassian.design/foundations/content/designing-messages/success-messages"
        }}>{`success messages`}</a>{`
and `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/modal-dialog/usage"
        }}>{`modal dialogs`}</a></li>
      <li parentName="ul">{`Social interactions and while introducing new experiences`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      